// ?edit=1 on Steps 2 and 3: what is selected in the pane, the overrides by building, and
// whether a value is written over (docs/Y2026-Q4/design-amazon-overview-overrides.md).
import { createContext, useContext } from "react";

import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import { drifted, type Overrides, platformValue } from "./overrides";

/** What the pane shows. A wording carries the text the page writes, the platform value an
 * override of it is set against; a computed value names the targets it is worked out from. */
export type Target =
  | { kind: "step"; projectId: string; stepId: string }
  | { kind: "building"; projectId: string }
  | { kind: "contract"; projectId: string }
  | { kind: "need"; projectId: string; key: string }
  | { kind: "ask"; projectId: string; actionId: string; title: string }
  | { kind: "wording"; projectId: string; key: string; label: string; pageText: string }
  | { kind: "computed"; projectId: string; label: string; from: Target[] }
  | { kind: "stepOne"; stepId: string };

export const sameTarget = (a: Target | null, b: Target | null): boolean => {
  if (!a || !b || a.kind !== b.kind) return false;
  const id = (t: Target) =>
    t.kind === "stepOne"
      ? t.stepId
      : `${t.projectId}|${"stepId" in t ? t.stepId : "key" in t ? t.key : "actionId" in t ? t.actionId : "label" in t ? t.label : ""}`;
  return id(a) === id(b);
};

export interface Edit {
  editing: boolean;
  selected: Target | null;
  select: (t: Target | null) => void;
  overrides: (projectId: string) => Overrides;
  /** The building as the platform has it, no override applied. */
  platform: (projectId: string) => AmazonBuilding | undefined;
  /** Shows the step in Step 1's pane, where its name, owner and bullets are edited. */
  openStepOne: (stepId: string) => void;
}

export const EditContext = createContext<Edit | null>(null);

export const useEdit = (): Edit => {
  const edit = useContext(EditContext);
  if (!edit) throw new Error("useEdit outside EditProvider");
  return edit;
};

export type Mark = "none" | "set" | "drift";

/** Whether any of ``keys`` is written over on the building, and whether the platform has
 * moved under one of its facts since. */
export const markOf = (edit: Edit, projectId: string, keys: string[]): Mark => {
  const o = edit.overrides(projectId);
  const set = keys.filter((k) => o.has(k));
  if (!set.length) return "none";
  const raw = edit.platform(projectId);
  return raw && set.some((k) => drifted(o.get(k), platformValue(raw, k))) ? "drift" : "set";
};

